import * as React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useLiveQuery } from "dexie-react-hooks";
import { getGoals } from "../../../database/queries/goals";
import type { Goal } from "src/database/types";
import { WeekReport } from "src/database/models/GoalSummary";
import { GoalAccordionSummary } from "./GoalAccordionSummary";
import { useGoalSpotlight } from "./GoalSpotlightContext";
import { useIsoContext } from "src/isoContext";
import { MOBILE_STICKY_HEADER_PX, PAGE_LAYER_PORTAL_ID } from "src/components/Page";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { AnyActionableItem, TreeOf } from "src/types";

interface Props {
  items: TreeOf<AnyActionableItem>[];
}

function flatten(items: TreeOf<AnyActionableItem>[]): AnyActionableItem[] {
  return items.flatMap(({ children, ...item }) => [item, ...flatten(children)]);
}

function AccordionGoalRows({
  displayedGoals,
  goalSummaryIndex,
}: {
  displayedGoals: Goal[];
  goalSummaryIndex: Record<string, WeekReport>;
}) {
  return (
    <GoalList>
      {displayedGoals.map((goal) => (
        <GoalItem key={goal.id}>
          <GoalName>{goal.name}</GoalName>
          <GoalAccordionSummary summary={goalSummaryIndex[goal.id]} />
        </GoalItem>
      ))}
    </GoalList>
  );
}

export function GoalAccordion({ items }: Props) {
  const { spotlightGoalIds } = useGoalSpotlight();
  const { isLarge } = useIsoContext();
  const goals = useLiveQuery(() => getGoals(), []);
  const instances = React.useMemo(
    () => flatten(items).filter((item) => item.type === "instance"),
    [items],
  );
  const [isExpanded, setIsExpanded] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [accordionInScrollView, setAccordionInScrollView] = React.useState(true);

  const spotlightOn = spotlightGoalIds.length > 0;
  const hadSpotlightRef = React.useRef(false);
  React.useEffect(() => {
    if (spotlightOn) {
      setIsExpanded(true);
    } else if (hadSpotlightRef.current) {
      setIsExpanded(false);
    }
    hadSpotlightRef.current = spotlightOn;
  }, [spotlightOn]);

  const goalSummaryIndex = React.useMemo(() => {
    if (!goals || !instances) return {};
    const byGoal: Record<string, WeekReport> = {};
    for (const instance of instances) {
      const goalId = instance.goalId;
      if (!goalId) continue;
      const row = (byGoal[goalId] ??= { total: 0, completed: 0 });
      row.total += 1;
      if (instance.completed) row.completed += 1;
    }
    return byGoal;
  }, [goals, instances]);

  const todayGoals = React.useMemo(() => {
    if (!goals) return undefined;
    return goals.filter((g) => g.id in goalSummaryIndex && goalSummaryIndex[g.id].total > 0);
  }, [goals, goalSummaryIndex]);

  const displayedGoals = React.useMemo(() => {
    if (!todayGoals?.length) return [];
    if (spotlightGoalIds.length === 0) return todayGoals;
    return todayGoals.filter((g) => spotlightGoalIds.includes(g.id));
  }, [todayGoals, spotlightGoalIds]);

  const hasAccordion = displayedGoals.length > 0;

  React.useEffect(() => {
    const target = containerRef.current;
    if (!hasAccordion || !target) {
      return;
    }
    // Viewport root: scrolling the page `Main` column still moves nodes vs the viewport.
    const rootMargin = isLarge ? "0px" : `-${MOBILE_STICKY_HEADER_PX}px 0px 0px 0px`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setAccordionInScrollView(entry.isIntersecting);
      },
      { root: null, rootMargin, threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [isLarge, hasAccordion]);

  const showFloatingSpotlight = spotlightOn && !accordionInScrollView;

  if (!hasAccordion) return null;

  const rowsProps = { displayedGoals, goalSummaryIndex };

  return (
    <>
      <Container ref={containerRef}>
        <Header onClick={() => setIsExpanded(!isExpanded)}>
          <Title>Daily Goals</Title>
          {isExpanded ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
        </Header>
        <Content $expanded={isExpanded && !showFloatingSpotlight}>
          <AccordionGoalRows {...rowsProps} />
        </Content>
      </Container>
      {showFloatingSpotlight &&
        typeof document !== "undefined" &&
        createPortal(
          <FloatingSheet role="status" aria-live="polite" aria-label="Goal progress">
            <AccordionGoalRows {...rowsProps} />
          </FloatingSheet>,
          document.getElementById(PAGE_LAYER_PORTAL_ID) ?? document.body,
        )}
    </>
  );
}

const Container = styled.div`
  background-color: var(--box-color);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.125);
  margin: 0 -10px;
  position: relative;
  @media (min-width: 600px) {
    margin: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  padding: 10px 20px;
  @media (min-width: 600px) {
    padding: 10px;
  }
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 300;
  margin: 0;
`;

const Content = styled.div<{ $expanded: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--box-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  z-index: 1;
  display: ${({ $expanded }) => ($expanded ? "flex" : "none")};
  flex-direction: column;
  gap: 1rem;
  border-top: 1px solid var(--box-color-contrast);
  padding: 10px 20px;
  @media (min-width: 600px) {
    padding: 10px;
  }
`;

const FloatingSheet = styled.div`
  width: 100%;
  padding: 10px 20px;
  background-color: var(--box-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  @media (min-width: 600px) {
    padding: 10px;
  }
`;

const GoalList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const GoalItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const GoalName = styled.span`
  font-size: 14px;
  font-weight: 300;
`;
